/**
 * ActionRequestComponents
 * 
 * Componenti UI per creare e visualizzare richieste sensibili.
 * 
 * REGOLA: Le richieste si possono creare SOLO con accesso verificato.
 */

import { useState } from 'react';
import { 
  Edit3, XCircle, HelpCircle, FileQuestion, 
  Clock, CheckCircle, Eye, X, AlertTriangle,
  Send, ChevronDown, ChevronUp, Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

/**
 * Icons per tipo richiesta
 */
const REQUEST_TYPE_ICONS = {
  change_request: Edit3,
  cancellation_request: XCircle,
  support_request: HelpCircle,
  document_help_request: FileQuestion
};

/**
 * Icons per stato richiesta
 */
const REQUEST_STATUS_ICONS = {
  submitted: Clock,
  under_review: Eye,
  approved: CheckCircle,
  rejected: XCircle,
  resolved: CheckCircle,
  cancelled_by_user: X
};

/**
 * Pulsanti azione per singolo elemento
 */
export function ItemActionButtons({ 
  itemId, 
  isVerified, 
  policy,
  hasPendingRequest,
  onRequestAction,
  onRequestVerification
}) {
  if (!isVerified) {
    return (
      <div className="flex items-center gap-2 text-xs text-umbria-muted">
        <Shield size={12} />
        <button 
          onClick={onRequestVerification}
          className="text-umbria-olive hover:underline"
        >
          Verifica per richiedere modifiche
        </button>
      </div>
    );
  }

  if (hasPendingRequest) {
    return (
      <div className="flex items-center gap-2 text-xs text-amber-600">
        <Clock size={12} />
        <span>Richiesta in corso</span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      <Button
        size="sm"
        variant="outline"
        className="text-xs h-7 px-2"
        onClick={() => onRequestAction(itemId, 'change_request')}
      >
        <Edit3 size={12} className="mr-1" />
        Modifica
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="text-xs h-7 px-2 text-red-600 hover:text-red-700"
        onClick={() => onRequestAction(itemId, 'cancellation_request')}
      >
        <XCircle size={12} className="mr-1" />
        Cancella
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className="text-xs h-7 px-2"
        onClick={() => onRequestAction(itemId, 'support_request')}
      >
        <HelpCircle size={12} className="mr-1" />
        Assistenza
      </Button>
    </div>
  );
}

/**
 * Modal per creare una richiesta
 */
export function RequestModal({
  isOpen,
  onClose,
  itemId,
  itemTitle,
  itemType,
  requestType,
  policy,
  onSubmit,
  submitting
}) {
  const [message, setMessage] = useState('');
  const [payload, setPayload] = useState({});
  const [confirmed, setConfirmed] = useState(false);

  if (!isOpen) return null;

  const TypeIcon = REQUEST_TYPE_ICONS[requestType] || HelpCircle;
  
  const typeLabels = {
    change_request: 'Richiesta modifica',
    cancellation_request: 'Richiesta cancellazione',
    support_request: 'Richiesta assistenza',
    document_help_request: 'Aiuto documenti'
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (requestType === 'cancellation_request' && !confirmed) return;
    
    const result = await onSubmit(itemId, requestType, message, payload);
    if (result?.success) {
      onClose();
      setMessage('');
      setPayload({});
      setConfirmed(false);
    }
  };

  // Check policy warnings
  const getPolicyWarning = () => {
    if (!policy) return null;
    
    if (requestType === 'change_request') {
      const modLabel = policy.modification?.label || '';
      if (modLabel.includes('Non consentita')) {
        return 'Le modifiche non sono normalmente consentite per questo servizio.';
      }
    }
    
    if (requestType === 'cancellation_request') {
      const cancLabel = policy.cancellation?.label || '';
      if (cancLabel.includes('Non rimborsabile')) {
        return 'Questo servizio non è rimborsabile secondo le condizioni.';
      }
      if (cancLabel.includes('Non consentita')) {
        return 'La cancellazione non è normalmente consentita.';
      }
      if (cancLabel.includes('Con condizioni')) {
        const note = policy.cancellation?.note;
        return note || 'La cancellazione potrebbe comportare condizioni specifiche.';
      }
    }
    
    return null;
  };

  const policyWarning = getPolicyWarning();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-md w-full max-h-[90vh] overflow-y-auto">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <TypeIcon size={20} className="text-umbria-olive" />
              {typeLabels[requestType]}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X size={18} />
            </Button>
          </div>
          <p className="text-sm text-umbria-muted mt-1">
            {itemTitle}
          </p>
        </CardHeader>
        
        <CardContent>
          {/* Policy Summary */}
          {policy && (
            <div className="bg-umbria-stone/5 rounded-lg p-3 mb-4 text-xs">
              <p className="font-medium text-umbria-charcoal mb-2">
                Regole del servizio
              </p>
              <div className="space-y-1 text-umbria-muted">
                <p>Modifica: <span className={policy.modification?.color}>{policy.modification?.label}</span></p>
                <p>Cancellazione: <span className={policy.cancellation?.color}>{policy.cancellation?.label}</span></p>
                {policy.cancellation?.note && (
                  <p className="italic">{policy.cancellation.note}</p>
                )}
              </div>
            </div>
          )}

          {/* Policy Warning */}
          {policyWarning && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex gap-2">
              <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">{policyWarning}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Change Request specific fields */}
            {requestType === 'change_request' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-umbria-charcoal mb-1">
                    Nuova data preferita (opzionale)
                  </label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-umbria-stone/30 rounded-lg text-sm"
                    onChange={(e) => setPayload(p => ({ ...p, requested_date: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-umbria-charcoal mb-1">
                    Nuovo orario preferito (opzionale)
                  </label>
                  <input
                    type="time"
                    className="w-full px-3 py-2 border border-umbria-stone/30 rounded-lg text-sm"
                    onChange={(e) => setPayload(p => ({ ...p, requested_time: e.target.value }))}
                  />
                </div>
              </div>
            )}

            {/* Message field */}
            <div>
              <label className="block text-sm font-medium text-umbria-charcoal mb-1">
                {requestType === 'cancellation_request' ? 'Motivo (opzionale)' : 'Messaggio'}
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full px-3 py-2 border border-umbria-stone/30 rounded-lg text-sm resize-none"
                rows={3}
                placeholder={
                  requestType === 'change_request' ? 'Descrivi la modifica richiesta...' :
                  requestType === 'cancellation_request' ? 'Motivo della cancellazione...' :
                  'Come possiamo aiutarti?'
                }
                required={requestType !== 'cancellation_request'}
              />
            </div>

            {/* Cancellation confirmation */}
            {requestType === 'cancellation_request' && (
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="confirm-cancel"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="mt-1"
                />
                <label htmlFor="confirm-cancel" className="text-sm text-umbria-muted">
                  Confermo di aver letto le condizioni e desidero richiedere la cancellazione
                </label>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={onClose}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-umbria-olive hover:bg-umbria-olive/90"
                disabled={
                  submitting || 
                  (requestType === 'cancellation_request' && !confirmed) ||
                  (requestType !== 'cancellation_request' && !message.trim())
                }
              >
                {submitting ? (
                  <>
                    <Clock size={16} className="mr-2 animate-spin" />
                    Invio...
                  </>
                ) : (
                  <>
                    <Send size={16} className="mr-2" />
                    Invia richiesta
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Badge stato richiesta
 */
export function RequestStatusBadge({ status, statusDisplay }) {
  const display = statusDisplay?.[status] || { label: status, color: 'bg-gray-100 text-gray-600' };
  const StatusIcon = REQUEST_STATUS_ICONS[status] || Clock;
  
  return (
    <Badge className={`${display.color} gap-1 text-xs`}>
      <StatusIcon size={12} />
      {display.label}
    </Badge>
  );
}

/**
 * Card singola richiesta
 */
export function RequestCard({ request, typeDisplay, statusDisplay, onCancel }) {
  const [expanded, setExpanded] = useState(false);
  
  const TypeIcon = REQUEST_TYPE_ICONS[request.request_type] || HelpCircle;
  const typeInfo = typeDisplay?.[request.request_type] || { label: request.request_type };
  
  const createdDate = new Date(request.created_at).toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <div className="border border-umbria-stone/20 rounded-lg p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2">
          <div className={`p-1.5 rounded ${typeInfo.color || 'bg-gray-100'}`}>
            <TypeIcon size={14} />
          </div>
          <div>
            <p className="font-medium text-sm text-umbria-charcoal">
              {typeInfo.label}
            </p>
            <p className="text-xs text-umbria-muted">
              {request.item_title}
            </p>
          </div>
        </div>
        <RequestStatusBadge status={request.status} statusDisplay={statusDisplay} />
      </div>
      
      <div className="mt-2 flex items-center justify-between text-xs text-umbria-muted">
        <span>{createdDate}</span>
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 hover:text-umbria-charcoal"
        >
          {expanded ? 'Nascondi' : 'Dettagli'}
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
      </div>
      
      {expanded && (
        <div className="mt-3 pt-3 border-t border-umbria-stone/10 text-sm">
          {request.message && (
            <p className="text-umbria-charcoal mb-2">{request.message}</p>
          )}
          {request.status_note && (
            <p className="text-xs text-umbria-muted italic">
              Nota: {request.status_note}
            </p>
          )}
          {request.can_cancel && (
            <Button
              size="sm"
              variant="outline"
              className="mt-2 text-xs text-red-600"
              onClick={() => onCancel(request.id)}
            >
              <X size={12} className="mr-1" />
              Annulla richiesta
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Sezione richieste per il journey
 */
export function RequestsSection({ 
  requests, 
  summary, 
  typeDisplay, 
  statusDisplay,
  isVerified,
  onCancel,
  onRequestVerification
}) {
  if (!isVerified) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Edit3 size={18} className="text-umbria-olive" />
            Le tue richieste
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <Shield size={24} className="text-umbria-muted mx-auto mb-2" />
            <p className="text-sm text-umbria-muted mb-3">
              Verifica il tuo accesso per vedere e creare richieste
            </p>
            <Button 
              size="sm" 
              variant="outline"
              onClick={onRequestVerification}
            >
              Verifica accesso
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Edit3 size={18} className="text-umbria-olive" />
            Le tue richieste
          </CardTitle>
          {summary.pending > 0 && (
            <Badge className="bg-amber-100 text-amber-700">
              {summary.pending} in attesa
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {requests.length === 0 ? (
          <p className="text-sm text-umbria-muted text-center py-4">
            Nessuna richiesta inviata
          </p>
        ) : (
          <div className="space-y-3">
            {requests.map(req => (
              <RequestCard
                key={req.id}
                request={req}
                typeDisplay={typeDisplay}
                statusDisplay={statusDisplay}
                onCancel={onCancel}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default {
  ItemActionButtons,
  RequestModal,
  RequestStatusBadge,
  RequestCard,
  RequestsSection
};
